<template>
  <v-card class="rounded-lg fill-height d-flex flex-column">
    <v-card-title class="ml-2 flex-shrink-0">
      <div
        class="d-flex flex-nowrap align-center"
        style="width: 100%; gap: 8px"
      >
        <div
          class="subtitle-2 grey--text text--grey-100 mr-auto text-truncate"
          style="min-width: 0"
        >
          {{ $t('card_title.diagnostics_trend') }}
        </div>
        <template v-if="!removable">
          <v-autocomplete
            v-model="variables.selected"
            solo
            multiple
            chips
            small-chips
            deletable-chips
            :items="sortedVariableItems"
            :loading="variables.loading"
            dense
            item-value="var_id"
            :item-text="variableLabel"
            :label="$t('dropdown.select_variables_to_show.title')"
            hide-details
            :menu-props="{ maxHeight: 300 }"
            style="max-width: 300px; flex: 0 1 240px"
            @change="onVariablesChanged"
          >
            <template v-slot:selection="{ index }">
              <span
                v-if="index === 0"
                class="text-grey text-caption align-self-center"
              >
                {{ $tc('dropdown.number_of_selected_variables', variables.selected.length, { num: variables.selected.length }) }}
              </span>
            </template>
          </v-autocomplete>
          <v-btn-toggle
            v-model="dateRange"
            color="primary"
            mandatory
            @change="onPeriodChanged"
          >
            <v-btn
              small
              :value="1"
            >
              {{ $t('button.month_one') }}
            </v-btn>
            <v-btn
              small
              :value="3"
            >
              {{ $t('button.month_three') }}
            </v-btn>
            <v-btn
              small
              :value="6"
            >
              {{ $t('button.month_six') }}
            </v-btn>
            <v-btn
              small
              :value="12"
            >
              {{ $t('button.year_one') }}
            </v-btn>
            <v-btn
              small
              :value="60"
            >
              {{ $t('button.year_five') }}
            </v-btn>
          </v-btn-toggle>
          <v-btn-toggle
            v-model="view"
            color="primary"
            mandatory
          >
            <v-btn
              small
              class="text-none"
              value="visual"
            >
              {{ $t('card.display_option.visual') }}
            </v-btn>
            <v-btn
              small
              class="text-none"
              value="list"
            >
              {{ $t('card.display_option.list') }}
            </v-btn>
          </v-btn-toggle>
        </template>
        <v-btn
          v-if="removable"
          icon
          class="ml-auto"
          style="pointer-events: auto"
          @click="$emit('remove')"
        >
          <v-icon small>
            fal fa-times
          </v-icon>
        </v-btn>
      </div>
    </v-card-title>
    <v-card-text
      v-if="view === 'list' && !removable"
      class="flex-grow-1 pa-0"
      style="min-height: 0; overflow: auto;"
    >
      <v-data-table
        :headers="listHeaders"
        :items="rawData"
        :loading="stats.loading"
        fixed-header
        :items-per-page="-1"
        hide-default-footer
        dense
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              {{ $dayjs(item.start_at).format(getDateTimeFormatForUser('DD.MM.YYYY.')) }}
            </td>
            <td
              v-for="varId in variables.selected"
              :key="varId"
              class="font-weight-bold text-no-wrap"
            >
              <template v-if="item[varId] !== null && item[varId] !== undefined">
                {{ item[varId] }} {{ unitFor(varId) }}
                <v-icon
                  v-if="item[`${varId}_trend`] !== undefined"
                  small
                  :color="trendColour(item[`${varId}_trend`])"
                >
                  {{ trendIcon(item[`${varId}_trend`]) }}
                </v-icon>
              </template>
              <span
                v-else
                class="grey--text"
              >—</span>
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="text-grey text-caption pa-4">
            {{ $t('table.no_data') }}
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-text
      v-else
      class="flex-grow-1"
      style="position: relative; min-height: 0;"
    >
      <line-chart
        :raw-data="rawData"
        :series-override="seriesOverride"
        x-value="start_at"
        :chart-value-keys="chartValueKeys"
        class="elevation-0"
        :xaxis="{
          type: 'datetime',
          tickPlacement: 'between',
          min: dayjs(fromDate).valueOf(),
          max: dayjs(toDate).valueOf(),
          labels: { format: getDateTimeFormatForUser('ddd, dd. MMM') }
        }"
        :tooltip="{
          y: {
            formatter: (value, { seriesIndex, w }) => {
              if (value === null || value === undefined) return 'N/A';
              const series = w.config.series[seriesIndex];
              const seriesKey = series && series.name ? series.name : '';
              const chartKey = chartValueKeys.find(k => k.label === seriesKey);
              let unit = '';
              if (chartKey && chartKey.unit !== null && chartKey.unit !== undefined) {
                unit = chartKey.unit;
              }
              return `${value} ${unit}`.trim();
            }
          }
        }"
        :legend="{ showForSingleSeries: true }"
      />
      <div
        v-if="!removable && (variables.loading || stats.loading)"
        class="d-flex justify-center align-center"
        style="position: absolute; inset: 0; background: rgba(255,255,255,0.6); pointer-events: none;"
      >
        <v-progress-circular
          indeterminate
          size="64"
          width="6"
          color="grey lighten-2"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import { DiagnosticVariableApiService, PlayerApiService, TeamApiService } from '@/data/services'
import { getDateTimeFormatForUser } from '@/utils/helpers'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'

export default {
  name: 'NoProtocolDiagnosticTrendChart',
  components: { LineChart },
  props: {
    chart: {
      type: Object,
      default: () => ({})
    },
    teamId: {
      type: Number,
      default: null
    },
    player: {
      type: Object,
      default: () => ({})
    },
    date: {
      type: Object,
      default: () => dayjs()
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    variables: {
      data: [],
      selected: [],
      loading: false
    },
    stats: {
      data: [],
      loading: false
    },
    dateRange: 3,
    view: 'visual'
  }),
  computed: {
    fromDate() {
      return dayjs(this.date).subtract(this.dateRange, 'month').format('YYYY-MM-DD')
    },
    toDate() {
      return dayjs(this.date).format('YYYY-MM-DD')
    },
    variableByCode() {
      const map = {}
      for (const v of this.variables.data) map[v.var_id] = v
      return map
    },
    sortedVariableItems() {
      const selectedSet = new Set(this.variables.selected)
      const selected = []
      const unselected = []
      for (const v of this.variables.data) {
        if (selectedSet.has(v.var_id)) selected.push(v)
        else unselected.push(v)
      }
      return [...selected, ...unselected]
    },
    chartValueKeys() {
      return this.variables.selected.map(varId => {
        const info = this.variableByCode[varId]
        return {
          key: varId,
          type: 'line',
          label: info ? this.variableLabel(info) : varId,
          unit: info ? info.unit : null
        }
      })
    },
    rawData() {
      // Each entry is one measurement day, newest first, with trend computed
      // by comparing each row to the next-older entry in the array — mirrors
      // PlayerDiagnosticTrendChart's rawData semantics.
      return this.stats.data.map((row, index) => {
        const data = { start_at: row.date }
        for (const varId of this.variables.selected) {
          const value = row.stats && row.stats[varId] !== undefined ? row.stats[varId] : null
          data[varId] = value
          if (index < this.stats.data.length - 1) {
            const older = this.stats.data[index + 1]
            const olderValue = older.stats && older.stats[varId] !== undefined ? older.stats[varId] : null
            if (value !== null && olderValue !== null) {
              data[`${varId}_trend`] = value - olderValue
            } else {
              data[`${varId}_trend`] = null
            }
          }
        }
        return data
      })
    },
    seriesOverride() {
      return this.chartValueKeys.map(key => ({
        type: key.type || 'line',
        name: key.label,
        data: this.rawData
          .map(stat => ({ x: dayjs(stat.start_at).valueOf(), y: stat[key.key] }))
          .filter(p => p.y !== null && p.y !== undefined)
      }))
    },
    listHeaders() {
      const headers = [
        { text: this.$t('picker.date.title'), value: 'start_at', width: '15%', sortable: false }
      ]
      for (const varId of this.variables.selected) {
        const info = this.variableByCode[varId]
        headers.push({ text: info ? this.variableLabel(info) : varId, value: varId, sortable: false })
      }
      return headers
    }
  },
  watch: {
    teamId() {
      if (!this.removable) this.fetchMeasuredVariablesDebounced()
    },
    date(newVal, oldVal) {
      if (!newVal || !oldVal) return
      if (dayjs(newVal).isSame(dayjs(oldVal), 'day')) return
      if (!this.removable) this.fetchMeasuredVariablesDebounced()
    }
  },
  created() {
    if (this.removable) {
      this.setupMockData()
      return
    }
    if (Array.isArray(this.chart.vars)) {
      this.variables.selected = [...this.chart.vars]
    }
    if (this.chart.period) {
      this.dateRange = this.chart.period
    }
    this.fetchMeasuredVariables()
  },
  methods: {
    dayjs,
    getDateTimeFormatForUser,
    variableLabel(v) {
      if (!v) return ''
      return v.test_name ? `${v.var} - ${v.test_name}` : v.var
    },
    unitFor(varId) {
      const info = this.variableByCode[varId]
      return info && info.unit ? info.unit : ''
    },
    trendIcon(trend) {
      if (trend === undefined || trend === null) return 'fal fa-minus'
      if (trend > 0) return 'fal fa-arrow-trend-up'
      if (trend < 0) return 'fal fa-arrow-trend-down'
      return 'fal fa-minus'
    },
    trendColour(trend) {
      if (trend === undefined || trend === null || trend === 0) return 'grey'
      return trend > 0 ? 'success' : 'error'
    },
    setupMockData() {
      this.variables.data = [
        { var_id: 'HR_REST', var: 'Resting HR', unit: 'bpm', test_name: null },
        { var_id: 'JUMP_H', var: 'Jump Height', unit: 'cm', test_name: 'CMJ' }
      ]
      this.variables.selected = ['HR_REST', 'JUMP_H']
      const mock = []
      for (let i = 0; i < 12; i++) {
        const day = dayjs(this.date).subtract(i, 'week').format('YYYY-MM-DD')
        mock.push({
          date: day,
          stats: { HR_REST: 60 + Math.floor(Math.random() * 10), JUMP_H: 40 + Math.random() * 10 }
        })
      }
      this.stats.data = mock.reverse()
    },
    emitSettings() {
      this.$emit('update:chart', {
        ...this.chart,
        vars: [...this.variables.selected],
        period: this.dateRange
      })
    },
    onVariablesChanged() {
      this.emitSettings()
      this.fetchStatsDebounced()
    },
    onPeriodChanged() {
      this.emitSettings()
      this.fetchMeasuredVariablesDebounced()
    },
    fetchMeasuredVariablesDebounced: debounce(function () {
      this.fetchMeasuredVariables()
    }, 200),
    fetchStatsDebounced: debounce(function () {
      this.fetchStats()
    }, 200),
    async fetchMeasuredVariables() {
      const params = {
        event_at_date_gte: this.fromDate,
        event_at_date_lte: this.toDate
      }
      if (this.teamId) {
        params.team_id = this.teamId
      } else if (this.player && this.player.id) {
        params.player_ids = [this.player.id]
      } else {
        return
      }
      this.variables.loading = true
      try {
        const response = await DiagnosticVariableApiService.getMeasured(params)
        this.variables.data = response.data.variables || []
        if (this.variables.selected.length === 0 && this.variables.data.length > 0) {
          this.variables.selected = [this.variables.data[0].var_id]
        }
      } finally {
        this.variables.loading = false
      }
      // Fetch stats directly (no debounce) so the chart renders as soon as
      // the measured-vars call resolves. Debouncing is only needed at the
      // user-input entry points to collapse rapid clicks.
      this.fetchStats()
    },
    async fetchStats() {
      if (this.variables.selected.length === 0) {
        this.stats.data = []
        return
      }
      if (!this.teamId && !(this.player && this.player.id)) return
      this.stats.loading = true
      try {
        let raw
        if (this.teamId) {
          raw = await TeamApiService.getStats(
            this.teamId,
            this.fromDate,
            this.toDate,
            { diagnostics: [...this.variables.selected] },
            { trend: true }
          )
        } else {
          raw = await PlayerApiService.getDailyStats({
            player_ids: [this.player.id],
            from: this.fromDate,
            to: this.toDate,
            stats_types_variables: { diagnostics: [...this.variables.selected] },
            trend: true
          })
        }
        const rows = Array.isArray(raw) ? raw : (raw && raw.data) || []
        // Backend returns one row per day (including empty ones) in ascending
        // order. For both chart and list we only care about measurement days,
        // and the list shows newest first.
        const withData = rows.filter(r => r && r.stats && Object.keys(r.stats).length > 0)
        this.stats.data = withData.sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
      } finally {
        this.stats.loading = false
      }
    }
  }
}
</script>
